import { Skeleton, Stack, Box } from '@mui/material';

type Props = {
  variant?: 'text' | 'rectangular' | 'rounded' | 'circular';
  count?: number;
  height?: number | string;
  width?: number | string;
  layout?: 'default' | 'card' | 'list' | 'profile' | 'handover' | 'comment' | 'user';
  spacing?: number;
};

export const TaskLoading = ({
  variant = 'rectangular',
  count = 3,
  height = 60,
  width = '100%',
  layout = 'default',
  spacing = 2,
}: Props) => {
  // Render different skeleton structures based on layout
  const renderSkeleton = () => {
    switch (layout) {
      case 'handover':
        // Handover card: title + chip + description + metadata
        return Array.from({ length: count }).map((_, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: index % 2 === 0 ? 'background.paper' : 'action.hover',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Skeleton variant="text" width="40%" height={28} />
              <Skeleton variant="rectangular" width={80} height={20} sx={{ borderRadius: 1 }} />
            </Box>
            <Skeleton variant="text" width="90%" height={20} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="70%" height={20} sx={{ mb: 1.5 }} />
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Skeleton variant="text" width={120} height={16} />
              <Skeleton variant="text" width={100} height={16} />
            </Box>
          </Box>
        ));

      case 'user':
        // User card: avatar + name + details
        return Array.from({ length: count }).map((_, index) => (
          <Box
            key={index}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
              <Skeleton variant="circular" width={48} height={48} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="60%" height={24} sx={{ mb: 0.5 }} />
                <Skeleton variant="text" width="40%" height={16} />
              </Box>
            </Box>
            <Skeleton variant="text" width="80%" height={16} sx={{ mb: 1 }} />
            <Skeleton variant="text" width="60%" height={16} />
          </Box>
        ));

      case 'comment':
        // Comment: avatar + user info + comment text
        return Array.from({ length: count }).map((_, index) => (
          <Box
            key={index}
            sx={{
              p: 2,
              mb: 2,
              borderRadius: 2,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Skeleton variant="circular" width={32} height={32} />
              <Box sx={{ flex: 1 }}>
                <Skeleton variant="text" width="30%" height={20} sx={{ mb: 0.5 }} />
                <Box sx={{ display: 'flex', gap: 2, mb: 1 }}>
                  <Skeleton variant="text" width={100} height={14} />
                  <Skeleton variant="text" width={120} height={14} />
                </Box>
                <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: 1 }} />
              </Box>
            </Box>
          </Box>
        ));

      case 'profile':
        // Profile form: field labels + inputs
        return (
          <Box sx={{ maxWidth: 600 }}>
            {Array.from({ length: count }).map((_, index) => (
              <Box key={index} sx={{ mb: 3 }}>
                <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1 }} />
                <Skeleton variant="rectangular" width="100%" height={40} sx={{ borderRadius: 1 }} />
              </Box>
            ))}
          </Box>
        );

      case 'list':
        // Simple list items
        return Array.from({ length: count }).map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'background.paper',
            }}
          >
            <Skeleton variant="circular" width={40} height={40} />
            <Box sx={{ flex: 1 }}>
              <Skeleton variant="text" width="60%" height={20} sx={{ mb: 0.5 }} />
              <Skeleton variant="text" width="40%" height={16} />
            </Box>
          </Box>
        ));

      case 'card':
        // Generic card layout
        return Array.from({ length: count }).map((_, index) => (
          <Box
            key={index}
            sx={{
              p: 3,
              borderRadius: 3,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <Skeleton variant="text" width="50%" height={24} sx={{ mb: 2 }} />
            <Skeleton variant="rectangular" width="100%" height={height} sx={{ borderRadius: 2, mb: 2 }} />
            <Skeleton variant="text" width="70%" height={16} />
          </Box>
        ));

      case 'default':
      default:
        // Simple skeleton blocks
        return Array.from({ length: count }).map((_, index) => (
          <Skeleton key={index} variant={variant} height={height} width={width} animation="wave" />
        ));
    }
  };

  return (
    <Box sx={{ width: '100%', p: 2 }}>
      <Stack spacing={spacing}>{renderSkeleton()}</Stack>
    </Box>
  );
};
